import type { TermsData } from './terms.types';

const data: TermsData = {
  seo: {
    title: 'Terms of Use | E-School Cambodia',
    description: 'Read the terms of use for accessing and using E-School Cambodia educational services.',
  },
  hero: {
    badge: 'Official Document',
    title: 'Terms of Use',
    subtitle: 'For Students and Parents/Guardians',
  },
  intro: {
    text: "These Terms of Use describe the rules for accessing and using E-School's educational Services.<br/><br/>They apply to Students and Parents/Guardians using the E-School application, website, and related Services.<br/><br/>By accessing or using E-School, you agree to follow these Terms of Use.",
  },
  sections: [
    {
      number: '1',
      title: 'Using E-School',
      paragraphs: [
        'Students may use E-School for authorized educational activities including:',
        '• Joining live classes;',
        '• Watching recorded video lessons;',
        '• Accessing courses and learning programs;',
        '• Reading and using library materials;',
        '• Taking QCM/multiple-choice tests;',
        '• Completing quizzes, exercises, and assessments;',
        '• Communicating with teachers and authorized users;',
        '• Reviewing learning progress;',
        '• Reviewing attendance;',
        '• Viewing test results;',
        '• Viewing study reports;',
        '• Receiving educational notifications and announcements; and',
        '• Using other available learning features.',
        'Parents/Guardians may also use educational features available to their accounts and may access information concerning their authorized linked children.',
      ],
    },
    {
      number: '2',
      title: 'Your Account',
      paragraphs: [
        'You must use an account that you are authorized to access.',
        'You must not:',
        '• Share your password or authentication information with unauthorized people;',
        '• Sell, rent, lend, or transfer your account;',
        "• Use another Student's account;",
        '• Pretend to be another user;',
        '• Attempt to gain unauthorized permissions; or',
        "• Attempt to access another user's private information.",
        'If you believe your account has been accessed without permission, you should notify E-School.',
      ],
    },
    {
      number: '3',
      title: 'Parent/Guardian Access',
      paragraphs: [
        "E-School may allow Parents/Guardians to link their accounts to their child's E-School account.",
        'Once properly linked, a Parent/Guardian may be able to see information such as:',
        '• Courses and learning programs;',
        '• Study activity;',
        '• Learning progress;',
        '• Attendance;',
        '• QCM and test results;',
        '• Scores;',
        '• Study reports; and',
        '• Other educational information.',
        "Parents/Guardians must only access a child's information where they are authorized to do so.",
        'E-School may require verification before creating or changing a parent-child relationship.',
      ],
    },
    {
      number: '4',
      title: 'Course Access',
      paragraphs: [
        'Not every E-School account has access to every course or learning Service.',
        'Access may depend on:',
        '• Enrollment;',
        '• Grade;',
        '• Subject;',
        '• Learning program;',
        '• Account status;',
        '• School or institution;',
        '• Access period;',
        '• Applicable Service plan; or',
        '• Other authorization provided by E-School.',
        'Users must not attempt to circumvent these access restrictions.',
        'Some Services may be free, while others may require enrollment, payment, subscription, or other authorization.',
      ],
    },
    {
      number: '5',
      title: 'Live Classes',
      paragraphs: [
        'Students may join live classes for courses in which they are authorized to participate.',
        'Students should:',
        '• Join using their own authorized account;',
        '• Follow classroom rules;',
        '• Respect the teacher;',
        '• Respect other Students;',
        '• Use communication features appropriately; and',
        '• Avoid behavior that disrupts the class.',
        'Live-class links, meeting IDs, passcodes, invitations, or other protected access information must not be shared with unauthorized people.',
        'Possession of live-class access information does not necessarily authorize a person to participate.',
        'E-School may verify whether a participant is authorized and may deny or remove unauthorized participants.',
      ],
    },
    {
      number: '6',
      title: 'Video Lessons',
      paragraphs: [
        'Video lessons are provided for authorized educational use.',
        'Unless specifically permitted by E-School, users must not:',
        '• Download protected videos using unauthorized methods;',
        '• Copy protected videos;',
        '• Upload them to another platform;',
        '• Sell or redistribute them;',
        '• Share protected access with unauthorized persons; or',
        '• Circumvent technical protections.',
      ],
    },
    {
      number: '7',
      title: 'Library',
      paragraphs: [
        "E-School's digital library may provide educational materials including books, documents, PDFs, worksheets, course notes, images, and other resources.",
        'Some materials may only be available for particular courses, grades, subjects, programs, or users.',
        'Users may access and use materials only to the extent permitted by E-School and the applicable content rights.',
        'Protected E-School library content must not be commercially redistributed without authorization.',
      ],
    },
    {
      number: '8',
      title: 'QCM, Tests and Assessments',
      paragraphs: [
        'Students should complete tests and assessments fairly.',
        'Students must not:',
        '• Have another person take a test for them;',
        "• Take a test using another Student's identity;",
        '• Manipulate their score or results;',
        '• Attempt to modify stored results;',
        '• Exploit system errors to obtain answers or an unfair advantage; or',
        '• Interfere with the testing system.',
        'E-School may record test attempts, answers, scores, completion information, and related educational activity for educational and reporting purposes.',
      ],
    },
    {
      number: '9',
      title: 'Communication',
      paragraphs: [
        'Communication features are intended to support education and appropriate interaction between authorized users.',
        'Users should communicate respectfully with:',
        '• Teachers;',
        '• Other Students;',
        '• E-School support personnel; and',
        '• Other authorized users.',
        'Users must not send or upload:',
        '• Threatening or abusive content;',
        '• Bullying or harassment;',
        '• Sexually inappropriate content;',
        '• Illegal material;',
        '• Spam;',
        '• Malicious files;',
        '• Fraudulent or deceptive information;',
        "• Content that violates another person's privacy; or",
        "• Content that infringes another person's rights.",
        'E-School may investigate reported content and take appropriate action.',
      ],
    },
    {
      number: '10',
      title: 'Study Reports',
      paragraphs: [
        'E-School may generate reports based on Student learning activity.',
        'Reports may contain information such as:',
        '• Course progress;',
        '• Lessons studied;',
        '• Video learning activity;',
        '• Live-class attendance;',
        '• Test and QCM results;',
        '• Scores;',
        '• Learning activities; and',
        '• Other educational indicators.',
        'Students may access their own reports.',
        'Authorized Parents/Guardians may access reports for their linked children.',
        'Study reports are intended to help Students and Parents/Guardians understand educational activity and progress. They are not guarantees of future academic performance.',
      ],
    },
    {
      number: '11',
      title: 'Respecting Privacy',
      paragraphs: [
        'Users must respect the privacy of Students, Parents/Guardians, teachers, and other E-School users.',
        'Users must not:',
        "• Attempt to access another user's private information;",
        "• Share another person's private information without authorization;",
        '• Misuse information obtained through E-School;',
        '• Record private communications without authorization where prohibited; or',
        '• Use E-School information to harass or harm another person.',
        'Parents/Guardians should use information concerning their children responsibly and in accordance with applicable law.',
      ],
    },
    {
      number: '12',
      title: 'Content Ownership',
      paragraphs: [
        'E-School educational materials may be owned by E-School or licensed from teachers, publishers, educational partners, or other content owners.',
        'Using E-School does not transfer ownership of that content to the user.',
        'Users receive only a limited right to access and use content according to the applicable E-School Service and access conditions.',
      ],
    },
    {
      number: '13',
      title: 'Fees and Access to Paid Services',
      paragraphs: [
        'Some E-School courses or Services may require payment or another form of authorized enrollment.',
        'The availability of paid Services and payment methods may vary by platform, device, country or region, account type, program, or version of E-School.',
        'Where a payment option is available, applicable pricing, access periods, subscription terms, and other conditions will be communicated separately.',
        'Users who have already obtained authorized access to a course or Service may access that content through their E-School account, subject to the applicable access conditions.',
        'Where an application store or other payment provider processes a transaction, its applicable payment, cancellation, and refund rules may also apply.',
      ],
    },
    {
      number: '14',
      title: 'Prohibited Activities',
      paragraphs: [
        'Users must not use E-School to:',
        '• Hack or attack E-School systems;',
        '• Gain unauthorized access;',
        '• Circumvent security controls;',
        '• Circumvent course or Service restrictions;',
        '• Manipulate tests, attendance, scores, or reports;',
        "• Access another user's account or information;",
        '• Spread malware or harmful software;',
        '• Abuse or harass other users;',
        '• Redistribute protected educational content without authorization;',
        '• Exploit security vulnerabilities; or',
        '• Perform unlawful activities.',
      ],
    },
    {
      number: '15',
      title: 'Suspension of Access',
      paragraphs: [
        'If a user seriously or repeatedly violates these Terms of Use, E-School may:',
        '• Issue a warning;',
        '• Remove inappropriate content;',
        '• Remove the user from a live class;',
        '• Restrict a feature;',
        '• Temporarily suspend the account; or',
        '• Terminate access in serious cases.',
        'E-School may take immediate action where reasonably necessary to protect Students, other users, or platform security.',
      ],
    },
    {
      number: '16',
      title: 'Technical Requirements',
      paragraphs: [
        'Some E-School Services require:',
        '• A supported device;',
        '• An internet connection;',
        '• A supported version of the E-School application;',
        '• Camera access for certain features;',
        '• Microphone access for certain features; or',
        '• Other technical requirements.',
        'Live classes and video streaming may require a sufficiently stable internet connection.',
        'Users are responsible for their own device and internet connectivity.',
      ],
    },
    {
      number: '17',
      title: 'Updates to E-School',
      paragraphs: [
        'E-School may update its application and Services to:',
        '• Add new learning features;',
        '• Improve existing features;',
        '• Improve security;',
        '• Fix technical problems;',
        '• Meet legal requirements; or',
        '• Improve the learning experience.',
        'Some older application versions may eventually stop being supported.',
      ],
    },
    {
      number: '18',
      title: 'Service Availability',
      paragraphs: [
        'E-School aims to keep its Services available and reliable but does not guarantee uninterrupted operation.',
        'Services may occasionally be unavailable because of maintenance, updates, internet or infrastructure problems, third-party services, security requirements, or other circumstances.',
      ],
    },
    {
      number: '19',
      title: 'Privacy Policy',
      paragraphs: [
        'These Terms of Use should be read together with the E-School Privacy Policy.',
        'The Privacy Policy explains how E-School handles personal information, including:',
        '• Student information;',
        '• Parent/Guardian information;',
        '• Account information;',
        '• Educational activity;',
        '• Learning progress;',
        '• Attendance;',
        '• Test and assessment information;',
        '• Study reports;',
        '• Communications;',
        '• Device and technical information; and',
        '• Other information associated with using E-School.',
      ],
    },
    {
      number: '20',
      title: 'Other E-School Policies',
      paragraphs: [
        'These Terms of Use should also be read together with other applicable E-School policies, including:',
        '• E-School Terms and Conditions;',
        '• E-School Privacy Policy;',
        '• Any applicable Refund and Cancellation Policy; and',
        '• Any specific rules provided for a course or Service.',
        'If specific terms are provided for a particular Service, those specific terms may apply in addition to these Terms of Use.',
      ],
    },
    {
      number: '21',
      title: 'Changes to These Terms of Use',
      paragraphs: [
        'E-School may update these Terms of Use when its Services, features, technology, business operations, or legal requirements change.',
        'The latest version and revision date will be made available through E-School.',
        'For significant changes, E-School may provide notice through the application, website, email, notification, or another appropriate method.',
      ],
    },
    {
      number: '22',
      title: 'Governing Law',
      paragraphs: [
        'These Terms of Use are governed by the laws of the Kingdom of Cambodia, subject to any mandatory rights that apply under applicable law.',
      ],
    },
    {
      number: '23',
      title: 'Contact Us',
      paragraphs: [
        'For questions or concerns about using E-School, contact:',
        '<strong>E-School Cambodia</strong>',
        'Email: info@e-schoolcambodia.com<br/>Website: https://e-schoolcambodia.com<br/>Address: Kings View Office Building, Keo Chenda Street, Phum 3, Sangkat Chroy Changvar, Khan Chroy Changvar, Phnom Penh, Kingdom of Cambodia',
        'By accessing or using E-School, you acknowledge that you have read, understood, and agree to follow these Terms of Use.',
      ],
    },
  ],
};

export default data;
